import type { Contact } from '@paintscout/api'
import type { ContactLeadSource } from '@paintscout/util/builder'
import { getLeadSources, uuid } from '@paintscout/util/builder'
import isNull from 'lodash/isNull'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'
import type { OptionsDocument, QuoteTag } from 'paintscout'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DropdownSelectOption } from '@paintscout/ui-business'

export const contactTypeOptions = [
  { name: 'individual', label: 'Individual' },
  { name: 'company', label: 'Company' }
]

// Handle use/updating of contact values for ContactForm
export const useContactValues = ({
  contact,
  options
}: {
  contact: Partial<Contact>
  options: OptionsDocument
}): {
  contactValues: Contact
  contactType: string
  contactSources: ContactLeadSource[]
  setContactValues: (contact: Contact) => void
  setContactType: (type: string) => void
  handleCompanyChange: (option: DropdownSelectOption) => void
  handleSourceChange: (option: DropdownSelectOption) => void
  handlePreferredContactMethodChange: (option: DropdownSelectOption) => void
  handleTagChange: (tags: QuoteTag[]) => void
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  mapAddressToValues: (results: any) => void
  isDirty: boolean
} => {
  const initialState = getValues(contact, options)
  const [initialValues, setInitialValues] = useState<Contact>(initialState)
  const [values, setValues] = useState<Contact>(initialState)
  const [contactType, setContactType] = useState(contactTypeOptions[0].name)
  const prevContactKeyRef = useRef<string>(null)

  const handlePreferredContactMethodChange = useCallback((option: DropdownSelectOption) => {
    if (option?.value === '') {
      setValues((prev) => ({ ...prev, preferred_contact_method: null }))
    } else {
      setValues((prev) => ({ ...prev, preferred_contact_method: option?.value }))
    }
  }, [])

  const handleTagChange = useCallback((tags: QuoteTag[]) => {
    setValues((prev) => ({ ...prev, tags: tags.map((tag) => tag.name) }))
  }, [])

  const handleCompanyChange = useCallback((option: DropdownSelectOption) => {
    if (option?.value === '' || !option) {
      setValues((prev) => ({ ...prev, company: null, companyDocId: null }))
    } else {
      setValues((prev) => ({ ...prev, company: option?.label, companyDocId: option?.value }))
    }
  }, [])

  const handleSourceChange = useCallback((option: DropdownSelectOption) => {
    if (isNull(option) || !option) {
      setValues((prev) => ({ ...prev, lead_source: null }))
    } else {
      setValues((prev) => ({ ...prev, lead_source: option.value }))
    }
  }, [])

  const handleFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target
    setValues((prev) => ({ ...prev, [id]: value }))
  }, [])

  const mapAddressToValues = useCallback((results: any) => {
    // map sourced address results to values on selection
    const { municipality, postalCode, region, address, country } = results
    setValues((prev) => {
      return {
        ...prev,
        city: municipality ?? prev.city,
        postal: postalCode ?? prev.postal,
        province: region ?? prev.province,
        address: address ?? prev.address,
        country: country || prev.country
      }
    })
  }, [])

  const contactSources = getLeadSources({ options, type: 'leadSources' }).map((source) => {
    return {
      value: source.label,
      label: source.label,
      name: source.name
    }
  })

  useEffect(() => {
    const contactKey = `${contact?._id ?? ''}:${contact?._rev ?? ''}`
    if (prevContactKeyRef.current === contactKey) {
      return
    }
    prevContactKeyRef.current = contactKey
    const normalized = getValues(contact, options)
    setValues(normalized)
    setInitialValues(normalized)
    if (contact) {
      setContactType(contact.company ? contactTypeOptions[1].name : contactTypeOptions[0].name)
    }
  }, [contact, options])

  const isDirty = useMemo(() => !isEqual(values, initialValues), [values, initialValues])

  return {
    contactValues: values,
    contactType,
    contactSources,
    setContactValues: setValues,
    setContactType,
    handleCompanyChange,
    handlePreferredContactMethodChange,
    handleSourceChange,
    handleFieldChange,
    mapAddressToValues,
    handleTagChange,
    isDirty
  }
}

function getValues(contact?: Partial<Contact>, options?: OptionsDocument): Contact {
  const sanitized = omitBy(contact, isNull)

  return {
    _id: uuid(),
    _rev: null,
    _deleted: null,
    first_name: '',
    last_name: '',
    company: '',
    companyDocId: null,
    address: '',
    city:
      options.options.defaultContactDetails?.active && options.options.defaultContactDetails.city
        ? options.options.defaultContactDetails.city
        : '',
    province:
      options.options.defaultContactDetails?.active && options.options.defaultContactDetails.prov
        ? options.options.defaultContactDetails.prov
        : '',
    postal: '',
    phone_number: '',
    phone_ext: '',
    email: '',
    internal_note: '',
    secondary_first_name: '',
    secondary_last_name: '',
    alternate_email: '',
    alternate_phone: '',
    alternate_phone_ext: '',
    created_by: null,
    created_date: null,
    updated_date: null,
    source_id: null,
    source_user_id: null,
    source: null,
    lead_source: null,
    ...sanitized,
    tags: Array.isArray(sanitized.tags) ? sanitized.tags : [],
    type: 'contact',
    contact_type: 'client'
  }
}
